import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router";
import { AppRoutes } from "./app/routes";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { DebugProvider } from "./features/debug/debug-mode";
import { SHAREPOINT_CONSENT_PATH, SharepointConsentPage } from "./features/project/sharepoint-consent";
import { LoginScreen } from "./screens/LoginScreen";

const queryClient = new QueryClient();

/**
 * The auth gate. Routing only exists behind a session — an unauthenticated
 * visitor gets the login card regardless of the URL, and lands on Today once
 * they sign in.
 */
function Gate() {
  const { status } = useAuth();

  // The one page outside the sign-in: Microsoft sends an organisation's admin
  // here after they consent to GroundBrain's app, and they have no account
  // (`plans/sharepoint-sync.md`). It reads its address and calls nothing.
  if (window.location.pathname === SHAREPOINT_CONSENT_PATH) return <SharepointConsentPage />;

  if (status === "loading") {
    return (
      <div className="flex h-screen items-center justify-center bg-chrome-deep">
        <p className="font-display text-[0.8rem] tracking-[0.1em] text-white/60 uppercase">
          Loading…
        </p>
      </div>
    );
  }

  if (status !== "authenticated") return <LoginScreen />;

  return (
    <DebugProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </DebugProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </QueryClientProvider>
  );
}
